import { ReactNode, useCallback } from 'react';
import { Formik, FormikProps, FormikConfig, FormikHelpers } from 'formik';
import { Alert, Tooltip, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import {
  getMsgOfNoData,
  mustache2SlateValuePolicyMsgTypes,
} from 'components/TemplateEditor/mustacheUtils';
import { What } from 'views/Marketing/components/PolicyTable';
import { PolicyFormValues } from './share';
import NameField, { validate as nameValidate } from '../PolicyForm/NameField';
import EnableField, { validate as enableValidate } from '../PolicyForm/EnableField';
import TermOfValidityField from '../PolicyForm/TermOfValidityField';
import BilingualExecuteContextsFieldV2, {
  validate as bilingualExecuteContextsValidate,
} from '../PolicyForm/BilingualExecuteContextsFieldV2';
import ShippingStatusField, {
  validate as shippingStatusValidate,
} from '../PolicyForm/ShippingStatusField';
import AutoCancelField, { validate as autoCancelValidate } from '../PolicyForm/AutoCancelField';
import CancelTradeContentsField, {
  validate as cancelTradeContentsValidate,
} from '../PolicyForm/CancelTradeContentsField';
import StarsField, { validate as starsValidate } from '../PolicyForm/StarsField';
import AutoReviewContentsField, {
  validate as autoReviewContentsValidate,
} from '../PolicyForm/AutoReviewContents';
import AutoReviewDelayDay from '../PolicyForm/AutoReviewDelayDay';
import {
  cancellingTradeScenarios,
  isMarketingScenarioType,
  isNoRegionScenario,
  isPositiveReviewScenarioType,
  isReviewManagementScenarioType,
  lazadaUnLimitMessageCountScenarios,
  negativeReviewStars,
  positiveReviewStars,
  scenarioPermissionMap,
  tiktokCancelTradeScenarios,
} from '../config';
import ItemLimitField from '../PolicyForm/ItemLimitField';
import ReturnTypeField from '../PolicyForm/ReturnTypeField';
import DelayShipDelayDayField from '../PolicyForm/DelayShipDelayDayField';
import PayMethodField from '../PolicyForm/PayMethodField';
import OrderAmountField from '../PolicyForm/OrderAmountField';
import {
  disable1Platform,
  isFilterEnable1ScenarioType,
  isFilterEnable3ScenarioType,
} from '../TaskForm/FilterConsultBuyer';
import {
  getFilterNegativeBuyerDefault,
  isFilterNegativeBuyerScenarioSupport,
} from '../TaskForm/FilterNegativeBuyer';
import { StyledForm, supportLogisticsScenarios, useMsgTypes } from '../PolicyForm';
import StoreScopesField from '../TaskForm/StoreScopesField';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import StoresField from './StoresField';
import TimeoutReplyField from '../TaskForm/TimeoutReplyField';
import WelcomeReplyField from '../TaskForm/WelcomeReplyField';
import FilterRuleField from '../TaskForm/FilterRuleField';
import InviteFollowerField from '../TaskForm/InviteFollowerField';
import FilterAfterSaleField from '../TaskForm/FilterAfterSaleField';
import PraiseTriggerPointField from '../TaskForm/PraiseTriggerPointField';
import Conflict from '../TaskForm/Conflict';
import RoundConfigField from './RoundConfigField';
import { useActivityContext } from '@knowledge/Activity/ActivityContext';
import StatusButton from 'components/StatusButton';
import { getActivityDetail } from '@knowledge/service/api/activity';
import dayjs from 'lib/dayjs';
import { regionTimeZoneMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { newScenarioTypes } from '@knowledge/Activity/PolicyForm/share';
import RefundAmountField from '../PolicyForm/RefundAmountField';
import ChooseOps, { validate as refundRatioValidate } from '../PolicyForm/ChooseOps';

type FV = PolicyFormValues;

export type PolicyFormHelpers = FormikHelpers<FV>;

type FormChildren<FV> = (form: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface PolicyFormProps extends FormikConfig<FV> {
  children: FormChildren<FV>;
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  existNameList: string[];
  disabled?: boolean;
  replyOption?: Marketing.ReplyOption;
  onUpdate?: () => void;
}

export const createDefaultInitialValues = (
  scenarioType: Marketing.AllScenarioType,
  platform: General.PlatformUpperCase,
  round: number,
  region: General.UpperCaseRegion | 'ALL',
  shouldPassFilterRegularRefundBuyer: boolean,
): FV => {
  const context =
    isTiKTokUpperCase(platform) && supportLogisticsScenarios.includes(scenarioType)
      ? {
          content: [
            getMsgOfNoData('text'),
            getMsgOfNoData('logistics', region === 'ALL' ? undefined : region),
          ],
          translatedContent: [
            getMsgOfNoData('text'),
            getMsgOfNoData('logistics', region === 'ALL' ? undefined : region),
          ],
        }
      : {
          content: mustache2SlateValuePolicyMsgTypes(''),
          translatedContent: mustache2SlateValuePolicyMsgTypes(''),
        };
  return {
    scenarioType,
    platform,
    region,
    roundConfig: {
      executeTime: [scenarioType === 'PRAISE_REMINDER' ? 0 : 5, 10, 30, 30][round - 1],
      executeTimeUnit: 1,
    },
    isDefault: false,
    stores: [],
    scenarioConfig: {
      praiseTaskConfig: {
        triggerPoint:
          scenarioType === 'PRAISE_REMINDER'
            ? platform === 'TIKTOK'
              ? 'SIGNED'
              : 'CONFIRMED'
            : undefined,
      },
      welcomeReplyTaskConfig: {
        coolDownHour: 24,
      },
      timeoutReplyTaskConfig: {
        coolDownHour: 6,
        executeTime: platform === 'TOKOPEDIA' ? 12 : 1,
        executeTimeUnit: '2',
      },
      inviteFollowerTaskConfig: {
        limitDay: 30,
        limitCount: 5,
      },
    },
    filterHasConversation: scenarioType !== 'CANCELLING_DELIVERY_FAIL',
    regionStoreList: [],
    filterRule: {
      filterConsultBuyer: {
        filterWaitProcessConversation:
          isFilterEnable1ScenarioType(scenarioType) && platform !== disable1Platform,
        filterConsult: false,
        filterConsultAfterCreateOrder: isFilterEnable3ScenarioType(scenarioType),
        filterConsultDays: 1,
      },
      filterNegativeBuyer: isFilterNegativeBuyerScenarioSupport(scenarioType, platform)
        ? getFilterNegativeBuyerDefault(scenarioType)
        : false,
      filterAfterSale: {
        enable: scenarioType === 'ORDER_REMINDER',
        confirmDays: 90,
        outOfOrderItem: true,
      },
      filterRegularRefundBuyer: shouldPassFilterRegularRefundBuyer,
    },
    round,
    name: '',
    enable: true,
    termOfValidity: {
      type: 'LONG_TERM',
      dateTimeQuantum: null,
      timeQuantumGroup: null,
    },
    filterItem: {
      type: 'NO_FILTER',
      specificSPUs: [],
      specificSKUs: [],
      specificCategories: [],
    },
    bilingualExecuteContext: context,
    bilingualExecuteContexts: [context],
    regionExecuteContexts: [],
    executeItemContext: [],
    cancelTradeConfig: {
      shippingStatus: platform === 'SHOPEE' ? ['TO_PROCESS', 'PROCESSED'] : ['TO_PROCESS'],
      autoCancel: false,
      autoCancelLimitHour: 0,
      contents: [
        {
          sendPoint:
            (isTiKTokUpperCase(platform) &&
              tiktokCancelTradeScenarios.includes(scenarioType as any)) ||
            scenarioType === 'CANCELLING_DELIVERY_FAIL' ||
            platform === 'SHOPEE' ||
            platform === 'LAZADA'
              ? 'CANCELLED'
              : 'IN_CANCEL',
          executeContext: {
            content: mustache2SlateValuePolicyMsgTypes(''),
            translatedContent: mustache2SlateValuePolicyMsgTypes(''),
          },
        },
      ],
    },
    autoReviewConfig: {
      stars: isPositiveReviewScenarioType(scenarioType) ? positiveReviewStars : negativeReviewStars,
      contents: platform === 'LAZADA' ? ['ANY'] : ['ANY', 'EMPTY'],
      delayAutoReview:
        ['SHOPEE', 'LAZADA'].includes(platform) && isPositiveReviewScenarioType(scenarioType),
      delayDay:
        ['SHOPEE', 'LAZADA'].includes(platform) && isPositiveReviewScenarioType(scenarioType)
          ? 5
          : 0,
    },
    limitItem: {
      type: 'ALL',
      specificItems: [],
    },
    delayShipPolicyConfig: {
      delayDay: 3,
    },
    limitRule: {
      limitPayMethod: {
        enable: false,
        payMethods: [],
      },
      limitOrderAmount: {
        enable: false,
        operate: 'GT',
        args: [0],
      },
      limitRefundOrderAmount: {
        enable: false,
        operate: 'GT',
        args: [0],
      },
      limitOrderStatus: {
        enable: false,
        orderStatus: [],
      },
    },
    contentItemList: [],
    returnTradeConfig: {
      returnType: undefined!,
      autoRefund: false,
      refundRatio: 0,
    },
  };
};

function PolicyForm(props: PolicyFormProps) {
  const {
    children,
    scenarioType,
    platform,
    region,
    existNameList,
    disabled,
    replyOption,
    onUpdate,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const isCancellingTrade = cancellingTradeScenarios.includes(
    scenarioType as Marketing.CancellingTradeScenarioType,
  );

  const isReviewManagement = isReviewManagementScenarioType(scenarioType);

  const isNoRegion = isNoRegionScenario(scenarioType);

  const { show } = useActivityContext();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      if (isCancellingTrade || scenarioType === 'CANCELLING_DELIVERY_FAIL') {
        nameValidate(errors, values, t, existNameList);
        enableValidate(errors, values, t);
        if (platform === 'SHOPEE' && scenarioType !== 'CANCELLING_DELIVERY_FAIL') {
          shippingStatusValidate(errors, values, t);
          autoCancelValidate(errors, values, t);
        }
        cancelTradeContentsValidate(errors, values, t, platform);
        return errors;
      }

      if (isReviewManagement) {
        nameValidate(errors, values, t, existNameList);
        enableValidate(errors, values, t);
        starsValidate(errors, values, t);
        autoReviewContentsValidate(errors, values, t);
        bilingualExecuteContextsValidate(errors, values, t, platform, scenarioType);
        return errors;
      }

      if (isNoRegion) {
        if (scenarioType === 'RECEPTION_SCORE') {
          return errors;
        }

        nameValidate(errors, values, t, existNameList);
        enableValidate(errors, values, t);

        return errors;
      }

      nameValidate(errors, values, t, existNameList);
      enableValidate(errors, values, t);
      // bilingualExecuteContextValidate(errors, values, t, platform);
      !values.returnTradeConfig?.autoRefund &&
        bilingualExecuteContextsValidate(errors, values, t, platform, scenarioType);
      values.returnTradeConfig?.autoRefund && refundRatioValidate(errors, values, t);
      return errors;
    },
    [t, scenarioType, isCancellingTrade, isNoRegion, isReviewManagement, platform, existNameList],
  );

  const msgTypes = useMsgTypes(platform, scenarioType);

  return (
    <Formik enableReinitialize validateOnMount validate={validate} {...otherProps}>
      {(formProps) => {
        const { values } = formProps;

        if (scenarioType === 'RECEPTION_SCORE' && !replyOption) {
          const form = (
            <StyledForm colon>
              <div style={{ marginTop: 0 }}>
                <NameField disabled={disabled} />
                <EnableField disabled={disabled} />
                <StoreScopesField
                  disabled={disabled}
                  platform={platform}
                  permId={permissionNameIdMap[scenarioPermissionMap[scenarioType]]}
                />
                <FilterRuleField
                  scenarioType={scenarioType}
                  platform={platform}
                  disabled={disabled}
                />
              </div>
            </StyledForm>
          );

          return children(form, formProps);
        }

        const sendContentTips = platform === 'LAZADA' &&
          !lazadaUnLimitMessageCountScenarios.includes(scenarioType) && (
            <Tooltip
              title={t(
                'aide.因Lazada平台限制，买家不回复消息最多允许店铺发送5条/天消息。策略中的发送内容最多可添加5条。',
              )}
            >
              <What style={{ textIndent: 0 }} />
            </Tooltip>
          );

        if (newScenarioTypes.includes(scenarioType)) {
          return children(
            <StyledForm>
              <div>
                <h3>
                  {t('reviewSetting.发送内容')}
                  {sendContentTips}
                </h3>
                <BilingualExecuteContextsFieldV2
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                  msgTypes={msgTypes}
                  round={values.round}
                />
              </div>
            </StyledForm>,
            formProps,
          );
        }

        const { knowledgeId } = values;

        const storesField = isNoRegionScenario(scenarioType) ? (
          <StoreScopesField
            disabled={disabled}
            platform={platform}
            permId={permissionNameIdMap[scenarioPermissionMap[scenarioType]]}
          />
        ) : (
          <StoresField
            disabled={disabled}
            scenarioType={scenarioType}
            platform={platform}
            region={region}
          />
        );
        const timeoutReplyField = scenarioType === 'TIMEOUT_REPLY' && (
          <TimeoutReplyField disabled={disabled} isPolicy platform={platform} />
        );
        const welcomeReplyField = scenarioType === 'WELCOME_REPLY' && (
          <WelcomeReplyField disabled={disabled} isPolicy />
        );
        const filterRuleField = (
          <FilterRuleField scenarioType={scenarioType} platform={platform} disabled={disabled} />
        );
        const inviteFollowerField = (
          <InviteFollowerField scenarioType={scenarioType} disabled={disabled} isPolicy />
        );
        const filterAfterSaleField = scenarioType === 'ORDER_REMINDER' && (
          <FilterAfterSaleField
            platform={platform}
            disabled={disabled}
            scenarioType={scenarioType}
            name="filterRule.filterAfterSale"
          />
        );
        const praiseTriggerPointField = scenarioType === 'PRAISE_REMINDER' &&
          platform !== 'MERCADO' && <PraiseTriggerPointField disabled={disabled} isPolicy />;
        const roundConfigField = (isMarketingScenarioType(scenarioType) ||
          scenarioType === 'INVITE_FOLLOWER') && (
          <RoundConfigField disabled={disabled} scenarioType={scenarioType} />
        );
        const conflict = (
          <Conflict isPolicy platform={platform} region={region} scenarioType={scenarioType} />
        );
        const knowledgeTips =
          knowledgeId && isTimeExpired(values) ? (
            <Alert
              banner
              type="info"
              style={{ marginBottom: '6px', background: '#F0F4FF' }}
              message={
                <div>
                  {t('activity_policy_tips')}
                  <StatusButton
                    type="link"
                    size="small"
                    onClick={() =>
                      getActivityDetail(knowledgeId)
                        .then((activity) => {
                          show({
                            activity,
                            onUpdate,
                            onUpdatePolicy: onUpdate,
                          });
                        })
                        .catch(() => {
                          message.error(t('phrase.获取数据失败，请稍后再试'));
                        })
                    }
                  >
                    {t('goto_update')}
                    {'>'}
                  </StatusButton>
                </div>
              }
            />
          ) : null;

        const taskFields = [
          storesField,
          timeoutReplyField,
          welcomeReplyField,
          filterRuleField,
          inviteFollowerField,
          filterAfterSaleField,
          praiseTriggerPointField,
          roundConfigField,
          conflict,
          knowledgeTips,
        ];

        if (isNoRegion) {
          return children(taskFields, formProps);
        }

        if (isCancellingTrade || scenarioType === 'CANCELLING_DELIVERY_FAIL') {
          const form = (
            <StyledForm colon>
              <div>
                {knowledgeTips}
                <h3>{t('aide.基础配置')}</h3>
                <NameField disabled={disabled} />
                <EnableField disabled={disabled} />
                {storesField}
                <TermOfValidityField
                  disabled={disabled}
                  scenarioType={scenarioType}
                  region={region}
                />
                <ReturnTypeField disabled={disabled} scenarioType={scenarioType} />
                <ItemLimitField
                  disabled={disabled}
                  stores={values.stores}
                  platform={platform}
                  region={region}
                />
                <PayMethodField
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                />
                <OrderAmountField disabled={disabled} scenarioType={scenarioType} region={region} />
                <RefundAmountField
                  disabled={disabled}
                  scenarioType={scenarioType}
                  region={region}
                  platform={platform}
                />
                {filterRuleField}
              </div>
              {platform === 'SHOPEE' && scenarioType !== 'CANCELLING_DELIVERY_FAIL' && (
                <div>
                  <h3>{t('aide.订单规则')}</h3>
                  <ShippingStatusField disabled={disabled} />
                  <AutoCancelField disabled={disabled} />
                </div>
              )}
              <div>
                <h3>
                  {t('reviewSetting.发送内容')}
                  {sendContentTips}
                </h3>
                <CancelTradeContentsField
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                  msgTypes={msgTypes}
                />
              </div>
            </StyledForm>
          );

          return children(form, formProps);
        }

        if (isReviewManagementScenarioType(scenarioType)) {
          const form = (
            <StyledForm colon>
              <div>
                {knowledgeTips}
                <h3>{t('aide.基础配置')}</h3>
                <NameField disabled={disabled} />
                <EnableField disabled={disabled} />
                {storesField}
                {(!knowledgeId || scenarioType !== 'POSITIVE_REVIEW_CARE') && (
                  <ItemLimitField
                    disabled={disabled}
                    stores={values.stores}
                    platform={platform}
                    region={region}
                    skuEnable={false}
                  />
                )}
                <PayMethodField
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                />
                <OrderAmountField disabled={disabled} scenarioType={scenarioType} region={region} />
                {filterRuleField}
              </div>
              <div>
                <h3>{t('aide.评价规则')}</h3>
                <StarsField
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                />
                <AutoReviewContentsField disabled={disabled} platform={platform} />
                <AutoReviewDelayDay disabled={disabled} scenarioType={scenarioType} />
              </div>
              <div>
                <h3>
                  {t('reviewSetting.发送内容')}
                  {sendContentTips}
                </h3>
                <BilingualExecuteContextsFieldV2
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                  msgTypes={msgTypes}
                  round={values.round}
                />
              </div>
            </StyledForm>
          );

          return children(form, formProps);
        }

        const form = (
          <StyledForm colon>
            <div>
              {knowledgeTips}
              <h3>{t('aide.基础配置')}</h3>
              <NameField disabled={disabled} />
              <EnableField disabled={disabled} />
              {storesField}
              <TermOfValidityField
                disabled={disabled}
                scenarioType={scenarioType}
                region={region}
              />
              <ReturnTypeField disabled={disabled} scenarioType={scenarioType} />
              {(!knowledgeId ||
                (scenarioType !== 'CONFIRM_TRADE' && scenarioType !== 'SIGN_REMINDER')) && (
                <ItemLimitField
                  disabled={disabled || Boolean(knowledgeId)}
                  skuEnable={scenarioType !== 'ORDER_REMINDER'}
                  stores={values.stores}
                  platform={platform}
                  region={region}
                />
              )}
              <PayMethodField
                disabled={disabled}
                scenarioType={scenarioType}
                platform={platform}
                region={region}
              />
              <OrderAmountField disabled={disabled} scenarioType={scenarioType} region={region} />
              <RefundAmountField
                disabled={disabled}
                scenarioType={scenarioType}
                region={region}
                platform={platform}
              />
              {filterRuleField}
              {filterAfterSaleField}
              {inviteFollowerField}
              {praiseTriggerPointField}
              {roundConfigField}
              {scenarioType === 'DELAY_SHIP' && <DelayShipDelayDayField disabled={disabled} />}
              {conflict}
              <ChooseOps platform={platform} scenarioType={scenarioType} />
            </div>
            {!values.returnTradeConfig?.autoRefund && (
              <div>
                <h3>
                  {t('reviewSetting.发送内容')}
                  {sendContentTips}
                </h3>
                <BilingualExecuteContextsFieldV2
                  disabled={disabled}
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                  msgTypes={msgTypes}
                  round={values.round}
                />
              </div>
            )}
          </StyledForm>
        );

        return children(form, formProps);
      }}
    </Formik>
  );
}

function isTimeExpired(values: FV) {
  const { scenarioType, region, regions, termOfValidity } = values;

  if (termOfValidity.type !== 'DATE_TIME_QUANTUM') {
    return false;
  }

  const { endDateTime } = termOfValidity.dateTimeQuantum || {};

  if (!endDateTime) {
    return false;
  }

  const now = dayjs();

  const isAfter = (region: General.UpperCaseRegion) =>
    dayjs.tz(endDateTime, regionTimeZoneMap[toLowerCase(region)]).isAfter(now, 'minutes');

  if (!isNoRegionScenario(scenarioType) && region !== 'ALL') {
    return isAfter(region);
  }

  if (!regions) {
    return false;
  }

  return regions.every(isAfter);
}

export default PolicyForm;
